import { useQuery } from "@tanstack/react-query";
import { orderBy } from "lodash-es";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import type { TicketStatusDetailsDto } from "@/api/types";
import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import type { ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { ContextMenu } from "@/components/ContextMenu/ContextMenu";
import { DeleteModal, useDeleteModal } from "@/components/DeleteModal/DeleteModal";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { ArrowRightIcon } from "@/components/Icons/ArrowRightIcon";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import type { ModalBaseProps } from "@/components/Modal/Modal";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { TicketStatus } from "@/components/Ticket/TicketStatus";
import { Button } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { Select } from "@/design-system/Select";
import { createDashboardColumnHelper } from "@/design-system/Table/helpers";
import { TABLE_COLUMN_SIZES } from "@/design-system/Table/helpers";
import { Table } from "@/design-system/Table/Table";
import { isDefined } from "@/helpers/util";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { usePermission } from "@/hooks/usePermission";
import { TABLE_KEYS, useResizableTable } from "@/hooks/useResizableTable";
import { useSlug } from "@/hooks/useSlug";
import { ticketStatusMutations } from "@/queries/ticket-statuses/mutations";
import { useTicketStatusQueries } from "@/queries/ticket-statuses/queryOptions";
import { routes } from "@/routes";
import { type TranslationFunction, useTranslation } from "@/translations";

import { canManageStatuses } from "../permissions";
import { getTicketStatusTranslation } from "../util/ticketStatusHelper";

export function TicketStatusListPage(): React.ReactNode {
  const slug = useSlug();
  const projectId = useProjectId();
  const { t } = useTranslation();
  const hasPermission = usePermission();
  const navigate = useNavigate();
  const ticketStatusQueries = useTicketStatusQueries();
  const { componentProps: deleteModalProps, openDeleteModal } = useDeleteModal<string>("ticket-status-delete-modal");
  const [statusToBeReplaced, setStatusToBeReplaced] = useState<TicketStatusDetailsDto>();

  const { data, error, isFetching } = useQuery(ticketStatusQueries.list());
  const deleteTicketStatus = ticketStatusMutations.useDeleteTicketStatus();
  const columns = useMemo(() => {
    const helper = createDashboardColumnHelper<TicketStatusDetailsDto>();

    return [
      helper.accessor("name", {
        header: t("page.ticket-status.list.name"),
        cell: (cell) => {
          return (
            <TicketStatus
              description={getTicketStatusLabel(t, cell.row.original)}
              labelColor={cell.row.original.color}
              variant="dot"
            />
          );
        },
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
        meta: {
          shouldSimulateRowClick: true,
          renderTruncatedTooltip: (cell) => getTicketStatusLabel(t, cell.row.original),
        },
      }),
      helper.accessor("type", {
        header: t("page.ticket-status.list.type"),
        cell: (cell) => getTicketStatusTranslation(t, cell.getValue()),
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("isDefault", {
        header: t("page.ticket-status.list.is-default"),
        cell: (cell) => (
          <span className="whitespace-nowrap">
            {cell.getValue()
              ? t("page.ticket-status.list.is-default.true")
              : t("page.ticket-status.list.is-default.false")}
          </span>
        ),
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("timesUsed", {
        header: t("page.ticket-status.list.times-used"),
        cell: (cell) => (
          <span>
            {t("page.ticket-status.list.times-used.value", {
              count: cell.getValue(),
            })}
          </span>
        ),
        minSize: TABLE_COLUMN_SIZES.COUNT.MIN,
        size: TABLE_COLUMN_SIZES.COUNT.DEFAULT,
      }),
      helper.display({
        id: "actions",
        cell: (cell) => {
          if (!hasPermission(canManageStatuses)) {
            return null;
          }
          const id = cell.row.original.id;
          const menuActions: ContextMenuItem[] = [
            {
              labels: {
                default: t("common.action.edit"),
              },
              value: "edit",
              icon: <Edit05Icon />,
              callbackFn: () =>
                void navigate(
                  routes.ticketStatuses.edit({
                    slug,
                    id,
                  }),
                ),
            },
            {
              labels: {
                default: t("common.action.delete"),
                disabled: t("page.ticket-status.list.need-at-least-one-of-type"),
              },
              value: "delete",
              icon: <Trash02Icon />,
              callbackFn: () => {
                if (cell.row.original.timesUsed > 0) {
                  setStatusToBeReplaced(cell.row.original);
                } else {
                  openDeleteModal(id);
                }
              },
              isDisabled: !!data && data.items.filter((x) => x.type === cell.row.original.type).length <= 1,
            },
          ];

          return <ContextMenu items={menuActions} />;
        },
        size: TABLE_COLUMN_SIZES.ACTION.DEFAULT,
        enableResizing: false,
        meta: {
          isAction: true,
        },
      }),
    ];
  }, [hasPermission, data, slug, navigate, openDeleteModal, t]);

  const tableInstance = useResizableTable(TABLE_KEYS.TICKET_STATUSES, {
    columns,
    data: data?.items ?? [],
    enableSorting: false,
  });

  if (error) {
    return <ErrorPage error={error} />;
  }

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={t("page.ticket-status.list.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.tickets.title"),
              to: routes.tickets.list({
                slug,
              }),
            },
            {
              name: t("page.ticket-status.list.title"),
            },
          ]}
        />
      }
      actions={
        <>
          <AskNobiButton
            defaultFilters={{ projectIds: [projectId] }}
            context={{ scope: "project", entity: "ticket-statuses", page: "list" }}
            suggestions={[
              t("component.ask-nobi-chat-drawer.ticket-statuses.suggestion.1"),
              t("component.ask-nobi-chat-drawer.ticket-statuses.suggestion.2"),
            ]}
          />
          {hasPermission(canManageStatuses) ? (
            <Button
              type="link"
              href={routes.ticketStatuses.create({
                slug,
              })}
              data-testid="create-status-link"
            >
              {t("page.ticket-status.list.action.create")}
            </Button>
          ) : null}
        </>
      }
    >
      <DeleteModal
        title={t("page.ticket-status.list.action.delete.confirmation")}
        description={t("page.ticket-status.list.action.delete.description")}
        onDelete={(id) =>
          deleteTicketStatus.mutateAsync({
            id,
          })
        }
        {...deleteModalProps}
      />
      {data ? (
        <StatusChangeModal
          isOpened={isDefined(statusToBeReplaced)}
          originalStatus={statusToBeReplaced}
          possibleStatuses={data.items}
          onOpenChange={() => setStatusToBeReplaced(undefined)}
          onSubmit={async (replacementStatusId) => {
            if (!statusToBeReplaced) {
              return;
            }
            await deleteTicketStatus.mutateAsync({
              id: statusToBeReplaced.id,
              replacementStatusId,
            });
          }}
        />
      ) : null}
      <Table
        table={tableInstance}
        getRowHref={(row) =>
          routes.ticketStatuses.edit({
            slug,
            id: row.original.id,
          })
        }
        isLoading={isFetching}
        readOnly={!hasPermission(canManageStatuses)}
        data-testid="ticket-status-list"
      />
    </DocumentPage>
  );
}

type StatusChangeModalProps = ModalBaseProps & {
  originalStatus?: TicketStatusDetailsDto;
  possibleStatuses: TicketStatusDetailsDto[];
  onSubmit: (replacementStatusId: string) => Promise<void> | void;
};

function StatusChangeModal({
  isOpened,
  onOpenChange,
  onSubmit,
  originalStatus,
  possibleStatuses,
}: StatusChangeModalProps): React.ReactNode {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [replacementStatus, setReplacementStatus] = useState<TicketStatusDetailsDto>();
  useEffect(() => {
    if (originalStatus) {
      // oxlint-disable-next-line react-hooks/set-state-in-effect
      setReplacementStatus(
        orderBy(
          possibleStatuses.filter((x) => x.id !== originalStatus.id),
          // Order by same type first, then by whether its default, otherwise use inProgress status
          [(x) => x.type !== originalStatus.type, (x) => !x.isDefault, (x) => x.type !== "inProgress"],
        )[0],
      );
    }
  }, [originalStatus, possibleStatuses]);
  const replacementStatuses = possibleStatuses
    // Can't replace with same status
    .filter((x) => x.id !== originalStatus?.id)
    // Can't replace with rated status (we'd be missing a rating on backend)
    .filter((x) => originalStatus?.type !== "rated" && x.type !== "rated");

  return (
    <ConfirmModal
      tone="error"
      title={t("page.ticket-status.list.replacement-modal.title")}
      description={t("page.ticket-status.list.replacement-modal.description")}
      isOpened={isOpened}
      onOpenChange={onOpenChange}
      onReject={() => onOpenChange(false)}
      onResolve={async () => {
        try {
          if (!replacementStatus) {
            return;
          }
          setIsSubmitting(true);
          await onSubmit(replacementStatus.id);
        } finally {
          setIsSubmitting(false);
          onOpenChange(false);
        }
      }}
      isLoading={isSubmitting}
      resolveBtnProps={{
        disabled: !replacementStatus,
        text: t("common.action.delete"),
        "data-testid": "ticket-status-replacement-modal-confirm",
      }}
      rejectBtnProps={{
        "data-testid": "ticket-status-replacement-modal-reject",
      }}
    >
      <Inline gap="md" align="center" justify="center" wrap={false}>
        {originalStatus ? (
          <>
            <span className="shrink-0">
              <TicketStatus
                description={getTicketStatusLabel(t, originalStatus)}
                labelColor={originalStatus.color}
                variant="dot"
              />
            </span>
            <span className="shrink-0 text-text-secondary">
              <ArrowRightIcon />
            </span>
          </>
        ) : null}
        <div className="w-control-narrow shrink-0">
          <Select
            selected={replacementStatus}
            disabled={isSubmitting}
            items={replacementStatuses}
            onChange={setReplacementStatus}
            keySelector={(x) => x.id}
            renderOption={(status) => (
              <TicketStatus description={getTicketStatusLabel(t, status)} labelColor={status.color} variant="dot" />
            )}
            usePortal
          />
        </div>
      </Inline>
    </ConfirmModal>
  );
}

function getTicketStatusLabel(t: TranslationFunction, status: TicketStatusDetailsDto): string {
  return status.isDefault ? getTicketStatusTranslation(t, status.type) : status.name;
}
